import { Controller } from '@hotwired/stimulus'
import { loadStripe } from '@stripe/stripe-js'
import { post } from '@rails/request.js'

export default class StripeCheckoutController extends Controller {
  static targets = ['submitButton', 'spinner', 'submitButtonText', 'paymentMessage', 'paymentElement', 'loadingSkeleton']
  static values = {
    checkoutSessionEndpoint: String,
    stripePublicApiKey: String,
    theme: { type: String, default: 'stripe' },
    locale: { type: String, default: 'auto' },
    submitButtonText: { type: String, default: 'Pay' }
  }

  async connect () {
    try {
      this.stripe = await loadStripe(this.stripePublicApiKeyValue, { locale: this.localeValue })
      await this.initializeStripeCheckout()
      this.element.addEventListener('submit', this.handleSubmit)
      this.submitButtonTarget.disabled = false
    } catch (error) {
      this.hideLoadingSkeleton()
      this.showMessage(error.message || 'Error loading payment form. Please refresh the page.')
    }
  }

  disconnect () {
    this.element.removeEventListener('submit', this.handleSubmit)
  }

  initializeStripeCheckout = async () => {
    const response = await post(
      this.checkoutSessionEndpointValue, { contentType: 'application/json', responseKind: 'json' }
    )
    const parsedResponse = await response.json

    if (parsedResponse.error) {
      throw new Error(parsedResponse.error)
    }

    this.checkout = this.stripe.initCheckout({
      clientSecret: parsedResponse.clientSecret, elementsOptions: { appearance: { theme: this.themeValue } }
    })

    const loadActionsResult = await this.checkout.loadActions()
    if (loadActionsResult.type === 'success') {
      this.actions = loadActionsResult.actions
      const session = loadActionsResult.actions.getSession()
      this.submitButtonTextTarget.textContent = `${this.submitButtonTextValue} ${session.total.total.amount}`
    }

    this.hideLoadingSkeleton()
    const paymentElement = this.checkout.createPaymentElement()
    paymentElement.mount(this.paymentElementTarget)
  }

  handleSubmit = async (e) => {
    e.preventDefault()
    this.setLoading(true)

    const { error } = await this.actions.confirm()

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    this.showMessage(error.message)

    this.setLoading(false)
  }

  // ------- UI helpers -------

  hideLoadingSkeleton () {
    if (this.hasLoadingSkeletonTarget) {
      this.loadingSkeletonTarget.remove()
    }
  }

  showMessage = (messageText) => {
    this.paymentMessageTarget.classList.remove('hidden')
    this.paymentMessageTarget.textContent = messageText
  }

  // Show a spinner on payment submission
  setLoading (isLoading) {
    if (isLoading) {
      // Disable the button and show a spinner
      this.submitButtonTarget.disabled = true
      this.spinnerTarget.classList.remove('hidden')
      this.submitButtonTextTarget.classList.add('hidden')
    } else {
      this.submitButtonTarget.disabled = false
      this.spinnerTarget.classList.add('hidden')
      this.submitButtonTextTarget.classList.remove('hidden')
    }
  }
}
